import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { Pause, Play } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { tip } from '../../publicRoute';

// THE TRANSPORT UNDER A MOVEMENT — play and the scrubber — the assembly phase's and the joint
// view's one bar, so a joint plays exactly as the assembly phase will play it. `t` is 0..1 over
// whatever the caller is playing: one sub-step there, a joint's sub-steps one after another here.
//
// IT HAD THE TWO ENDS AS NAMED BUTTONS AND A CLOCK. The ends are where the bar's own ends are,
// and how many seconds in a four-second movement is said by where the dot stands.

// ADVANCED AGAINST THE WALL CLOCK rather than by counting frames, so a dropped frame costs a frame
// and not a slower movement. `ms` is how long 0 → 1 takes; it stops at the end.
export function usePlayClock(
  playing: boolean,
  setPlaying: (on: boolean) => void,
  setT: Dispatch<SetStateAction<number>>,
  ms: number,
) {
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = (now - last) / ms;
      last = now;
      setT((prev) => {
        const next = prev + dt;
        if (next >= 1) { setPlaying(false); return 1; }
        return next;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, ms, setPlaying, setT]);
}

// THE SPEEDS THE SECOND BUTTON CYCLES THROUGH, from ×1: ×2, ×4, ×6, ×8, ×0.5, ×1 again.
// A caller offering fewer passes its own (the published tutorial: ×1 and ×2).
const SPEEDS = [0.5, 1, 2, 4, 6, 8];
export const nextSpeed = (s: number, speeds = SPEEDS) => speeds[(speeds.indexOf(s) + 1) % speeds.length];

export function ScrubBar({ t, playing, onToggle, onSeek, refuse, sections, speed, onSpeed }: {
  t: number;
  playing: boolean;
  onToggle: () => void;
  // AND THE SPEED, where the caller offers it: a second button beside play, lit while ×2 is on,
  // pressed to switch it on and off. It neither plays nor pauses: that is the first button's alone
  // (Louis 2026-10-22).
  // Pressed, the next of ×0.5, ×1, ×2, ×4, ×6, ×8, round again (see nextSpeed); it shows the one in force.
  speed?: number;
  onSpeed?: () => void;
  // Somewhere on the bar — an end pressed, or dragged to. Stops the playing; the caller's to say.
  onSeek: (t: number) => void;
  // WHY PLAY IS REFUSED, where it is (a movement that cannot be true): the button turns red and
  // says so; the scrubber still works.
  refuse?: string;
  // THE BAR CUT INTO EQUAL STRETCHES, one per name, each written inside its own (a joint's
  // sub-steps, played one after another).
  sections?: string[];
}) {
  const { t: tr } = useLanguage();
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onToggle}
        disabled={!!refuse}
        title={tip(refuse)}
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-700 shadow-sm transition-colors enabled:hover:border-blue-500 enabled:hover:text-blue-600 disabled:cursor-not-allowed disabled:border-red-400 disabled:text-red-500"
        aria-label={playing ? 'Pause' : 'Play'}
      >
        {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      </button>
      {onSpeed && (
        <button
          type="button"
          onClick={onSpeed}
          disabled={!!refuse}
          title={tip(refuse ?? tr('notice.assembly.playFast'))}
          aria-label={tr('notice.assembly.playFast')}
          className="inline-flex h-9 w-11 shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white text-xs font-semibold text-slate-700 shadow-sm transition-colors enabled:hover:border-blue-500 enabled:hover:text-blue-600 disabled:cursor-not-allowed disabled:border-red-400 disabled:text-red-500"
        >
          ×{speed ?? 1}
        </button>
      )}
      {sections ? <SectionedTrack t={t} sections={sections} onSeek={onSeek} label={tr('notice.assembly.scrub')} /> : (
        <input
          type="range"
          min={0}
          max={1}
          step={0.001}
          value={t}
          onChange={(e) => onSeek(Number(e.target.value))}
          className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600"
          aria-label={tr('notice.assembly.scrub')}
        />
      )}
      {/* AS WIDE AS PLAY ON THE OTHER SIDE, so the cut bar stands in the middle. */}
      {sections && <span className="w-9 shrink-0" aria-hidden />}
    </div>
  );
}

// THE BAR CUT INTO ITS STRETCHES: played in blue up to a thin line that is where the clock stands,
// each named in black. A press lands exactly where it is pressed, in the middle of a stretch too,
// and a drag from there scrubs.
// THE DOT'S SIZE, in pixels — and how far in from each end its centre stops, so at 0 and at 1 it
// sits within the bar rather than half over its end. The fill ends at its centre.
const DOT_PX = 18;
const along = (t: number) => `calc(${DOT_PX / 2}px + ${t} * (100% - ${DOT_PX}px))`;

function SectionedTrack({ t, sections, onSeek, label }: {
  t: number;
  sections: string[];
  onSeek: (t: number) => void;
  label: string;
}) {
  const track = useRef<HTMLDivElement>(null);
  const pressed = useRef(false);
  const at = (x: number) => {
    const r = track.current!.getBoundingClientRect();
    return Math.min(1, Math.max(0, (x - r.left - DOT_PX / 2) / (r.width - DOT_PX)));
  };
  return (
    <div
      ref={track}
      role="slider"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={1}
      aria-valuenow={t}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight') onSeek(Math.min(1, t + 0.01));
        else if (e.key === 'ArrowLeft') onSeek(Math.max(0, t - 0.01));
      }}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        pressed.current = true;
        onSeek(at(e.clientX));
      }}
      onPointerMove={(e) => { if (pressed.current) onSeek(at(e.clientX)); }}
      onPointerUp={() => { pressed.current = false; }}
      onPointerCancel={() => { pressed.current = false; }}
      className="relative h-4 w-full cursor-pointer touch-none select-none"
    >
      {/* THE BAR ITSELF, clipped to its rounded ends — the dot is not, and stands over its edges. */}
      <div className="absolute inset-0 overflow-hidden rounded-md bg-slate-200">
        <div className="absolute inset-y-0 left-0 bg-blue-500/80" style={{ width: along(t) }} />
        {/* The sections on the dot's own span, so a boundary is where the dot stands at it. */}
        <div className="absolute inset-y-0 flex" style={{ left: DOT_PX / 2, right: DOT_PX / 2 }}>
          {sections.map((name, k) => (
            <div
              key={`${name}-${k}`}
              className={`relative flex flex-1 items-center justify-center text-[10px] font-bold leading-none tabular-nums text-slate-900 ${k ? 'border-l border-white' : ''}`}
            >
              {name}
            </div>
          ))}
        </div>
        <div className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-blue-700" style={{ left: along(t) }} />
      </div>
      {/* THE DOT, where the clock stands, as a video player's — the line's own colour. */}
      <div
        className="pointer-events-none absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-700 shadow-sm"
        style={{ left: along(t), width: DOT_PX, height: DOT_PX }}
        aria-hidden
      />
    </div>
  );
}

// THE CARD A PLAYED MOVEMENT SITS IN — its viewer, the bar that plays it and its sign-off, together
// rather than loose on the page. One for the joint view and the assembly sub-steps alike.
const CARD = 'mx-auto flex w-full flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm';
export const PLAY_CARD = `${CARD} max-w-4xl`;
// AS WIDE AS THE PAGE ALLOWS, for a picture drawn in the desktop's shape (see formFactor).
export const WIDE_PLAY_CARD = `${CARD} max-w-[86rem]`;
